// Standard cost of a routing stage on its work center, shared by the routing form and the list.
//
//   manual            crew × labour rate
//   machine           crew × labour rate + the machine's hour rate
//   machine + labour  crew × labour rate + the machine's operators (total / average wage) + its hour rate
//
// A unit costs its run time (per unit, or per batch ÷ batch size) at that hourly cost; setup is
// paid once per run. A job work stage costs its job rate per unit instead.

/** What a stage's cost depends on: a form row or a saved stage, normalised. */
export interface StageCostInput {
    machineId?: string | null;
    operatorsRequired?: number | string | null;
    stageType?: string | null;
    /** Run time: per unit, or per batch on a PER_BATCH stage */
    runMinutes?: number | string | null;
    setupMinutes?: number | string | null;
    timeBasis?: string | null;
    batchSize?: number | string | null;
    jobRate?: number | string | null;
}

const isJobWork = (s: StageCostInput) => String(s.stageType || '').toUpperCase() === 'JOB_WORK';

/** ₹ per hour of the stage, or null until the work center (and a needed machine) is set. */
export function stageHourlyCost(wc: any, s: StageCostInput): number | null {
    if (!wc || isJobWork(s)) return null;
    const labour = (Number(s.operatorsRequired) || 0) * (Number(wc.std_labour_rate) || 0);
    if (!wc.machine_required) return labour;
    const m = (wc.machines || []).find((x: any) => String(x.id) === String(s.machineId));
    if (!m) return null;
    const machineRate = Number(m.machine_hour_rate) || 0;
    if (String(wc.type).toUpperCase() !== 'BOTH') return labour + machineRate;
    const operators = Number(wc.operator_wage_basis === 'AVG' ? m.operator_avg_wage : m.operator_total_wage) || 0;
    return labour + operators + machineRate;
}

/** Run minutes one unit takes on the stage. */
export function runMinutesPerUnit(s: StageCostInput): number {
    const minutes = Number(s.runMinutes) || 0;
    return String(s.timeBasis || '').toUpperCase() === 'PER_BATCH' ? minutes / (Number(s.batchSize) || 1) : minutes;
}

/** ₹ per unit: run time at the hourly cost, or the job rate of a job work stage. */
export function stageUnitCost(wc: any, s: StageCostInput): number | null {
    if (isJobWork(s)) return Number(s.jobRate) || 0;
    const hourly = stageHourlyCost(wc, s);
    const perUnit = runMinutesPerUnit(s);
    if (hourly == null || !perUnit) return null;
    return (perUnit / 60) * hourly;
}

/** ₹ for one setup of the stage (paid once per production run). */
export function stageSetupCost(wc: any, s: StageCostInput): number | null {
    if (isJobWork(s)) return 0;
    const hourly = stageHourlyCost(wc, s);
    if (hourly == null) return null;
    return ((Number(s.setupMinutes) || 0) / 60) * hourly;
}

export const money = (n: number) => `₹${(Math.round(n * 100) / 100).toLocaleString('en-IN')}`;
